/**
 * Rate Limit Indicator Component
 * Badge showing remaining messages with color coding
 * Following soft, modern design with smooth animations
 */

import React from 'react';
import { motion } from 'motion/react';
import { MessageSquare } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import type { RateLimitState } from '../hooks/useChatRateLimit';

interface RateLimitIndicatorProps {
  rateLimitState: RateLimitState | null;
  className?: string;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Get color classes based on rate limit state
 */
const getColorClasses = (color: string, isRateLimited: boolean) => {
  if (isRateLimited) {
    return {
      bg: 'bg-red-50 dark:bg-red-950/20',
      border: 'border-red-200 dark:border-red-800',
      text: 'text-red-700 dark:text-red-400',
      icon: 'text-red-600 dark:text-red-500',
    };
  }
  
  switch (color) {
    case 'green':
      return {
        bg: 'bg-emerald-50 dark:bg-emerald-950/20',
        border: 'border-emerald-200 dark:border-emerald-800',
        text: 'text-emerald-700 dark:text-emerald-400',
        icon: 'text-emerald-600 dark:text-emerald-500',
      };
    case 'yellow':
      return {
        bg: 'bg-amber-50 dark:bg-amber-950/20',
        border: 'border-amber-200 dark:border-amber-800',
        text: 'text-amber-700 dark:text-amber-400',
        icon: 'text-amber-600 dark:text-amber-500',
      };
    case 'orange':
      return {
        bg: 'bg-orange-50 dark:bg-orange-950/20',
        border: 'border-orange-200 dark:border-orange-800',
        text: 'text-orange-700 dark:text-orange-400',
        icon: 'text-orange-600 dark:text-orange-500',
      };
    default:
      return {
        bg: 'bg-gray-50 dark:bg-gray-950/20',
        border: 'border-gray-200 dark:border-gray-800',
        text: 'text-gray-700 dark:text-gray-400',
        icon: 'text-gray-600 dark:text-gray-500',
      };
  }
};

/**
 * Get size classes
 */
const getSizeClasses = (size: 'sm' | 'md' | 'lg') => {
  switch (size) {
    case 'sm':
      return {
        container: 'px-2 py-1 text-xs',
        icon: 'w-3 h-3',
        gap: 'gap-1.5',
      };
    case 'lg':
      return {
        container: 'px-3 py-1.5 text-sm',
        icon: 'w-4 h-4',
        gap: 'gap-2',
      };
    default: // md
      return {
        container: 'px-2.5 py-1 text-xs',
        icon: 'w-3.5 h-3.5',
        gap: 'gap-1.5',
      };
  }
};

/**
 * Rate Limit Indicator Component
 * Displays remaining messages with color-coded badge
 */
export const RateLimitIndicator: React.FC<RateLimitIndicatorProps> = ({
  rateLimitState,
  className,
  showIcon = true,
  size = 'md',
}) => {
  if (!rateLimitState) {
    return null;
  }

  const { remaining, limit, color, isRateLimited } = rateLimitState;
  const colors = getColorClasses(color, isRateLimited);
  const sizes = getSizeClasses(size);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.2 }}
      className={cn(
        'inline-flex items-center rounded-lg border backdrop-blur-sm',
        'transition-all duration-200',
        colors.bg,
        colors.border,
        sizes.container,
        sizes.gap,
        className
      )}
      role="status"
      aria-live="polite"
      aria-label={`${remaining} of ${limit} messages remaining`}
    >
      {showIcon && (
        <MessageSquare 
          className={cn('shrink-0', colors.icon, sizes.icon)} 
          aria-hidden="true"
        />
      )}
      <span className={cn('font-medium tabular-nums', colors.text)}>
        {remaining}/{limit}
      </span>
    </motion.div>
  );
};

